import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties, ComponentProps, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vault } from '@sorare/core/src/atoms/icons/Vault';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXXS } from '@sorare/core/src/atoms/typography/index.tsx';
import { AnimatedBackgroundBox } from '@sorare/core/src/atoms/ui/AnimatedBackgroundBox';
import { CornerBadge } from '@sorare/core/src/components/CornerBadge/index.tsx';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags.tsx';

import { MatchesPlayedStat } from '#src/components/league/MatchesPlayedStat/index.tsx';
import { isFixtureLive } from '#src/lib/so5.ts';

import type { LeagueSquareLink_so5LeaderboardGroupInterface } from './__generated__/index.graphql.ts';
import { TopContainer } from './ui.tsx';

const Root = styled(Horizontal).attrs({ center: true })`
  position: relative;
  padding: var(--custom-padding, calc(5 * var(--unit)) var(--double-unit));
  height: 100%;
`;
const Logo = styled.img`
  object-fit: contain;
`;
const Content = styled(Vertical).attrs({ center: true, grow: true })`
  max-width: 100%;
`;

type Props = {
  so5LeaderboardGroupInterface: LeagueSquareLink_so5LeaderboardGroupInterface;
  logoProps?: {
    width?: number;
    height?: number;
  };
  rewardVariant?: 'top' | 'bottom';
  children?: ReactNode;
  topLeft?: ReactNode;
  topRight?: ReactNode;
  style?: CSSProperties;
} & Pick<
  ComponentProps<typeof AnimatedBackgroundBox>,
  'to' | 'onClick' | 'forceShowBackground' | 'className'
>;

export const LeagueSquareLink = ({
  so5LeaderboardGroupInterface,
  logoProps = {
    width: 180,
    height: 80,
  },
  children,
  topLeft,
  topRight,
  ...rest
}: Props) => {
  const {
    slug,
    displayName,
    primaryColor,
    logoUrl,
    tileBackgroundUrl,
    so5Fixture,
    vaultPointsRequired,
    hasLeaderboards,
  } = so5LeaderboardGroupInterface;

  const {
    flags: { boostedCompetitions = [] },
  } = useFeatureFlags();

  const isLive = isFixtureLive(so5Fixture);

  return (
    <div style={{ position: 'relative', isolation: 'isolate' }}>
      <AnimatedBackgroundBox
        key={slug}
        backgroundDarker={!hasLeaderboards}
        primaryColor={primaryColor}
        backgroundPictureUrl={tileBackgroundUrl}
        {...rest}
        className={classNames(rest.className, 'dark-mode')}
        topRightLabel={
          vaultPointsRequired ? (
            <Horizontal>
              <Vault size={1.5} color="var(--c-yellow-600)" />
              <FormattedMessage defaultMessage="Vault Level Req." />
            </Horizontal>
          ) : undefined
        }
      >
        <Root>
          <Content>
            <TopContainer>
              {topLeft}
              {topRight || (
                <>
                  {!!isLive && (
                    <MatchesPlayedStat
                      so5LeaderboardGroup={so5LeaderboardGroupInterface}
                    />
                  )}
                </>
              )}
            </TopContainer>
            <Logo
              src={logoUrl}
              alt={displayName}
              style={{
                opacity: hasLeaderboards ? 1 : 0.2,
              }}
              {...logoProps}
            />
            {children}
          </Content>
        </Root>
      </AnimatedBackgroundBox>

      {boostedCompetitions.includes(so5LeaderboardGroupInterface.slug) && (
        <div
          style={{
            position: 'absolute',
            top: 'var(--unit)',
            right: 0,
            zIndex: 1,
          }}
        >
          <CornerBadge
            color={`linear-gradient(323deg, #00FF88 14.3%, #00CC66 85.7%)`}
          >
            <img
              height={10}
              src={
                'https://frontend-assets.sorare.com/task_campaigns/campaigns/icon-boosted_rewards_2x.png'
              }
              alt={'Boosted 2X'}
            />
            <BrandXXS color="var(--c-black)">{'Boosted 2X'}</BrandXXS>
          </CornerBadge>
        </div>
      )}
    </div>
  );
};

LeagueSquareLink.fragments = {
  so5LeaderboardGroupInterface: gql`
    fragment LeagueSquareLink_so5LeaderboardGroupInterface on So5LeaderboardGroupInterface {
      slug
      displayName
      logoUrl
      hasLeaderboards
      vaultPointsRequired
      so5Fixture {
        slug
        ...isFixtureLive_so5Fixture
      }
      tileBackgroundUrl
      ...MatchesPlayedStat_so5LeaderboardGroupInterface
    }
    ${MatchesPlayedStat.fragments.so5LeaderboardGroupInterface}
    ${isFixtureLive.fragments.so5Fixture}
  ` as TypedDocumentNode<LeagueSquareLink_so5LeaderboardGroupInterface>,
};
